/**
 * SelectDetailsDialog Component
 * 
 * Dialog for selecting which details to enable for a category and setting default values.
 */

import React, { ReactElement } from "react";
import StandardDialog from "../../../components/StandardDialog";
import { SelectDetailsContent } from "./SelectDetailsContent";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

interface SelectDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  category: TransactionCategory;
  groups: TransactionCategoryGroup[];
  enabledDetails: string[];
  setEnabledDetails: (details: string[]) => void;
  defaultValues: Record<string, string | number>;
  setDefaultValues: (values: Record<string, string | number>) => void;
  originalEnabledDetails: string[];
  originalDefaultValues: Record<string, string | number>;
  savingDetails: boolean;
  error: string;
  isDetailUsed: (categoryName: string, detailName: string) => boolean;
  hasDetailSelectionChanged: (
    enabledDetails: string[],
    originalEnabledDetails: string[],
    defaultValues: Record<string, string | number>,
    originalDefaultValues: Record<string, string | number>
  ) => boolean;
}

/**
 * Dialog for selecting which details to enable for a category
 */
function SelectDetailsDialog({
  open,
  onClose,
  onConfirm,
  category,
  groups,
  enabledDetails,
  setEnabledDetails,
  defaultValues,
  setDefaultValues,
  originalEnabledDetails,
  originalDefaultValues,
  savingDetails,
  error,
  isDetailUsed,
  hasDetailSelectionChanged,
}: SelectDetailsDialogProps): ReactElement {
  const group = category.groupId
    ? groups.find((g) => g.id === category.groupId)
    : null;

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={`Select Details - ${category.name}`}
      confirmText={savingDetails ? "Saving..." : "Save"}
      onConfirm={onConfirm}
      confirmDisabled={
        savingDetails ||
        !hasDetailSelectionChanged(enabledDetails, originalEnabledDetails, defaultValues, originalDefaultValues)
      }
      maxWidth="sm"
      alert={error ? { severity: "error", message: error } : undefined}
    >
      <SelectDetailsContent
        category={category}
        group={group ?? null}
        enabledDetails={enabledDetails}
        setEnabledDetails={setEnabledDetails}
        defaultValues={defaultValues}
        setDefaultValues={setDefaultValues}
        isDetailUsed={isDetailUsed}
      />
    </StandardDialog>
  );
}

export default SelectDetailsDialog;
